import { IndexSpec } from './indexes';
import { IndexJobProgress, IndexJobRequest } from './jobRequest';
import { z, type ZodType } from 'zod';

export type JobState = z.infer<typeof JobState>;
export type JobSchedule = z.infer<typeof JobSchedule>;
export type JobHistoryItem = z.infer<typeof JobHistoryItem>;
export type Job<T, P, S> = z.infer<ReturnType<typeof Job<T, P, S>>>;
export type ApplyIndexJob = z.infer<typeof ApplyIndexJob>;

export const JobState = z.enum(['planned', 'completed', 'error']);

export const JobSchedule = z.union([
  z.object({ milliseconds: z.number() }),
  z.object({ seconds: z.number() }),
  z.object({ minutes: z.number() }),
  z.object({ hours: z.number() }),
  z.object({ days: z.number() }),
  z.object({ cron: z.string() }),
]);

export const JobHistoryItem = z.object({
  t: z.number(),
  attempt: z.number(),
  event: z.enum(['start', 'complete', 'error', 'log']),
  message: z.string().optional(),
});

export const Job = <TData, TProgress, TResult>(data: ZodType<TData>, progress: ZodType<TProgress>, result: ZodType<TResult>) =>
  z.object({
    _id: z.string(),
    jobId: z.string(),
    lock: z.coerce.date().nullish(),
    state: JobState,
    attempt: z.number(),
    data: data,
    finishedOn: z.coerce.date().nullish(),
    nextRun: z.coerce.date().nullish(),
    progress: progress.nullish(),
    schedule: JobSchedule.nullish(),
    error: z.string().nullish(),
    result: result.nullish(),
    history: JobHistoryItem.array().nullish(),
  });

export const AnyJob = Job(z.unknown(), z.unknown(), z.unknown());
export const ApplyIndexJob = Job(IndexJobRequest, IndexJobProgress, IndexSpec);

export const applyIndexJobId = 'applyIndex';
/** Sub jobs run under their own job id so they can't be starved by top level jobs occupying all parallel slots. */
export const applyIndexSubJobId = 'applyIndexSub';
